import axios from 'axios';
import config from '@/config/constants';
import { auth } from '@/config/firebase';

/**
 * Thin wrapper around the /v1/marketing/* endpoints. The super-user Marketing
 * Console can be opened from the LOGIN screen (pre-tenant-selection), where the
 * super_user has a Firebase session but no Kiey JWT in localStorage yet. So we
 * carry BOTH credentials, matching the deployment super-admin tool: the Kiey
 * JWT (Authorization) when logged in AND the Firebase ID token
 * (X-Firebase-Token) when available. The backend require_super_user accepts
 * either, so the tool works both from the login screen and post-login.
 */
export async function authHeaders() {
  const headers = {};
  const token = (typeof localStorage !== 'undefined' && localStorage.getItem('auth_token')) || '';
  if (token) headers.Authorization = token;
  try {
    const fbUser = auth.currentUser;
    if (fbUser) headers['X-Firebase-Token'] = await fbUser.getIdToken();
  } catch (_) { /* no firebase session — JWT path only */ }
  return headers;
}

/**
 * Super-user "Marketing Console" can manage another tenant's marketing. Every
 * endpoint accepts an optional `target_tenant_id` (query param for GETs, body
 * field for POST/PUT). When `opts.tenantId` is falsy the param is omitted and
 * the backend behaves exactly as before (acts on the caller's own tenant).
 */
function targetParams(opts) {
  return opts && opts.tenantId ? { target_tenant_id: opts.tenantId } : {};
}

/**
 * Normalize an Axios error from the Cloud Functions envelope
 * ({ code: 'ERROR', errors: ... }) into a single human-readable string.
 * `errors` may be an array, a { field: [msg] } object, or a string.
 */
export function humanizeError(e) {
  if (!e) return 'Unknown error';
  const data = e.response && e.response.data;
  if (data && data.errors) {
    const errs = data.errors;
    if (Array.isArray(errs)) return errs.filter(Boolean).map(String).join(', ');
    if (typeof errs === 'string') return errs;
    if (typeof errs === 'object') {
      const parts = [];
      for (const k of Object.keys(errs)) {
        const v = errs[k];
        parts.push(Array.isArray(v) ? `${k}: ${v.join(', ')}` : `${k}: ${v}`);
      }
      if (parts.length) return parts.join(' · ');
    }
  }
  if (data && data.message) return data.message;
  if (data && typeof data === 'string') return data;
  return e.message || 'Unknown error';
}

export async function listWatchedPages(opts = {}) {
  const res = await axios.get(`${config.api_url}marketing/watched-pages`, {
    headers: await authHeaders(),
    params: targetParams(opts),
  });
  return (res.data && res.data.data && res.data.data.pages) || [];
}

export async function createWatchedPage({ url, label, platform }, opts = {}) {
  const res = await axios.post(
    `${config.api_url}marketing/watched-pages`,
    { url, label, platform, ...targetParams(opts) },
    { headers: await authHeaders() },
  );
  return res.data && res.data.data && res.data.data.page;
}

export async function updateWatchedPage(id, patch, opts = {}) {
  const res = await axios.put(
    `${config.api_url}marketing/watched-pages/${id}`,
    { ...patch, ...targetParams(opts) },
    { headers: await authHeaders() },
  );
  return res.data && res.data.data && res.data.data.page;
}

export async function archiveWatchedPage(id, opts = {}) {
  return updateWatchedPage(id, { archived: true }, opts);
}

export async function generateScreenshotReply({ image_base64, image_media_type, platform, agent_id }, opts = {}) {
  const body = { image_base64, image_media_type, platform, ...targetParams(opts) };
  if (agent_id) body.agent_id = agent_id;
  const res = await axios.post(
    `${config.api_url}marketing/screenshot-reply`,
    body,
    { headers: await authHeaders() },
  );
  return res.data && res.data.data;
}

export async function getDashboard(opts = {}) {
  const res = await axios.get(`${config.api_url}marketing/dashboard`, {
    headers: await authHeaders(),
    params: targetParams(opts),
  });
  return (res.data && res.data.data) || {};
}

/**
 * List the agents (owner + agent roles) of a tenant for the link-generation
 * agent pickers. Omit `opts.tenantId` for the caller's own tenant; pass it to
 * read another tenant's agents from the super-user Marketing Console. Returns
 * an array of `{ id, label, user_role }`.
 */
export async function listTenantAgents(opts = {}) {
  const res = await axios.get(`${config.api_url}marketing/tenant-agents`, {
    headers: await authHeaders(),
    params: targetParams(opts),
  });
  return (res.data && res.data.data && res.data.data.agents) || [];
}

/**
 * Super-admin only: list ALL watched pages ("Pages to watch") across every
 * tenant, grouped by tenant. Returns an array of `{ tenant_id, tenant_name,
 * page_count, pages }` where each page is `{ id, url, label, platform,
 * created_at }`. No `target_tenant_id` — the endpoint spans all tenants.
 */
export async function listAllWatchedPages() {
  const res = await axios.get(`${config.api_url}marketing/all-watched-pages`, {
    headers: await authHeaders(),
  });
  return (res.data && res.data.data && res.data.data.groups) || [];
}
